// Confirm delete modal component

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Confirm delete modal dialog
 * Shows a confirmation dialog before destructive actions
 */
class ConfirmDelete extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    heading: { type: String },
    message: { type: String },
    confirmLabel: { type: String, attribute: 'confirm-label' },
  };

  static styles = css`
    :host {
      display: contents;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1050;
    }

    .modal-dialog {
      max-width: 500px;
      width: 100%;
      margin: 1rem;
    }

    .modal-content {
      background-color: white;
      border-radius: 0.5rem;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      border-bottom: 1px solid #dee2e6;
    }

    .modal-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
    }

    .btn-close {
      padding: 0.5rem;
      background: transparent;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      opacity: 0.5;
    }

    .btn-close:hover {
      opacity: 0.75;
    }

    .modal-body {
      padding: 1rem;
    }

    .modal-body p {
      margin: 0;
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding: 1rem;
      border-top: 1px solid #dee2e6;
    }

    .btn {
      padding: 0.5rem 1rem;
      border-radius: 0.375rem;
      border: 1px solid transparent;
      font-size: 1rem;
      cursor: pointer;
      transition:
        background-color 0.15s,
        border-color 0.15s;
    }

    .btn-secondary {
      background-color: #6c757d;
      border-color: #6c757d;
      color: white;
    }

    .btn-secondary:hover {
      background-color: #5c636a;
      border-color: #565e64;
    }

    .btn-danger {
      background-color: #dc3545;
      border-color: #dc3545;
      color: white;
    }

    .btn-danger:hover {
      background-color: #bb2d3b;
      border-color: #b02a37;
    }
  `;

  constructor() {
    super();
    this.show = false;
    this.heading = 'Confirm Delete';
    this.message = 'Are you sure you want to delete this? This cannot be undone.';
    this.confirmLabel = 'Delete';
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  /** @param {KeyboardEvent} e */
  _onKeyDown(e) {
    if (e.key === 'Escape' && this.show) {
      e.stopImmediatePropagation();
      this._handleCancel();
    }
  }

  /** @param {Map<string, unknown>} changed */
  updated(changed) {
    if (changed.has('show')) {
      if (this.show) {
        document.addEventListener('keydown', this._onKeyDown, true);
      } else {
        document.removeEventListener('keydown', this._onKeyDown, true);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown, true);
  }

  _handleConfirm() {
    this.dispatchEvent(new CustomEvent('confirm', { bubbles: true }));
  }

  _handleCancel() {
    this.dispatchEvent(new CustomEvent('cancel', { bubbles: true }));
  }

  render() {
    if (!this.show) {
      return html``;
    }

    return html`
      <div class="modal-backdrop" @click="${this._handleCancel}">
        <div class="modal-dialog" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${this.heading}</h5>
              <button type="button" class="btn-close" @click="${this._handleCancel}">&times;</button>
            </div>
            <div class="modal-body">
              <p>${this.message}</p>
              <slot></slot>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="${this._handleCancel}">Cancel</button>
              <button type="button" class="btn btn-danger" @click="${this._handleConfirm}">${this.confirmLabel}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('confirm-delete', ConfirmDelete);
